"use client";

import { GovernmentAgencyCombobox } from "@entities/common";
import { GovernmentEmployerBasicRegistrationDto, governmentEmployerBasicRegistrationSchema } from "@entities/employer";
import { zodResolver } from "@hookform/resolvers/zod";
import { StorageFileUploader, useStorageFile } from "@shared/storage";
import { StorageBucket } from "@sisnaker-react-core/karirhub-model-type";
import {
  ComboboxOption,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from "@ubud-ui/react-ui-kit";
import { PropsWithChildren } from "react";
import { useForm } from "react-hook-form";

type FormProps = {
  formValue?: GovernmentEmployerBasicRegistrationDto | undefined;
  onSubmit?: (data: GovernmentEmployerBasicRegistrationDto) => void;
  hideAgency?: boolean;
};

export function GovernmentEmployerBasicRegistrationForm({ formValue, onSubmit, children, hideAgency }: FormProps & PropsWithChildren) {
  const form = useForm<GovernmentEmployerBasicRegistrationDto>({
    resolver: zodResolver(governmentEmployerBasicRegistrationSchema),
    defaultValues: {
      government_agency_id: formValue?.government_agency_id || undefined,
      name: formValue?.name || undefined,
      establishment_date: formValue?.establishment_date || undefined,
      phone: formValue?.phone || undefined,
      email: formValue?.email || undefined,
      website: formValue?.website || undefined,
      description: formValue?.description || undefined,
      logo: formValue?.logo || undefined,
    },
  });

  const { getInitialFile } = useStorageFile();
  const file = getInitialFile({ bucket: StorageBucket["karirhub-employer-logos"], fileId: formValue?.logo || undefined });

  const handleSubmit = (data: GovernmentEmployerBasicRegistrationDto) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="w-full" noValidate>
        <div className="md:grid md:grid-cols-2 md:gap-x-4 space-y-6">
          {!hideAgency && (
            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="government_agency_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Lembaga/Instansi <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl className="mt-2">
                      <div>
                        <GovernmentAgencyCombobox
                          initialValue={field.value as ComboboxOption}
                          onChange={(option: any) => {
                            field.onChange(option);
                            form.setValue("name", option?.label);
                          }}
                          onClear={() => {
                            field.onChange(null as any);
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="mt-2" />
                  </FormItem>
                )}
              />
            </div>
          )}

          <div className="md:col-span-2">
            <FormField
              control={form.control}
              name="establishment_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Tanggal berdiri <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <div>
                      <DatePicker
                        value={field.value || ""}
                        onSelect={(e) => field.onChange(e)}
                        onClear={() => field.onChange(null as any)}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />
          </div>

          <div className="md:col-span-2">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Deskripsi singkat entitas <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <Textarea
                      placeholder="Masukkan deskripsi singkat entitas"
                      className="h-24"
                      value={field.value || ""}
                      onInput={(e) => field.onChange(e)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Nomor telepon entitas <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl className="mt-2">
                  <Input
                    type="tel"
                    inputMode="tel"
                    value={field.value || ""}
                    placeholder="Cth.: 021123456"
                    onInput={(e) => field.onChange(e)}
                  />
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Email resmi entitas <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl className="mt-2">
                  <Input
                    type="email"
                    placeholder="Cth.: entitas@company.com"
                    value={field.value || ""}
                    onInput={(e) => field.onChange(e)}
                  />
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>
            )}
          />

          <div className="md:col-span-2">
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Situs web <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl className="mt-2">
                    <div className="flex items-stretch">
                      <div className=" bg-gray-50 px-3 py-2.5 rounded-tl-lg rounded-bl-lg border border-input text-sm h-10 border-r-0">
                        www.
                      </div>
                      <Input
                        type="url"
                        placeholder="Masukkan situs web entitas"
                        className="!rounded-tl-none !rounded-bl-none"
                        value={field.value || ""}
                        onInput={(e) => field.onChange(e)}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />
          </div>

          <div className="md:col-span-2">
            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Logo entitas <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="mt-2">
                      <StorageFileUploader
                        bucket={StorageBucket["karirhub-employer-logos"]}
                        maxFiles={1}
                        maxSize={5 * 1024 * 1024}
                        initialFiles={file}
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={({ uploadedFiles }) => {
                          if (uploadedFiles && uploadedFiles.length > 0) {
                            field.onChange(uploadedFiles[0]?.id);
                          } else {
                            field.onChange("");
                          }
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="mt-2" />
                </FormItem>
              )}
            />
          </div>
        </div>

        {children}
      </form>
    </Form>
  );
}
